import { useCreateStationeryDraftMutation } from './ProductDetailsPage.graphql.generated';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { useCardsRouterContext } from '@apps/card/Card.routes';

import { useExistingDraftsWarningContext } from '../ExistingDraftsWarningProvider';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addSuppressedError } from '@shared/utils/logger';
import { cloneDeep } from 'lodash-es';
import type { ThemeJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { withBasicDigitalV2Stamp } from '@apps/card/components/ProductUIConfig/digitalProductType';

import type { Format, MinimalRequiredTemplateDataForDraft } from '../DesignsGallery/DesignsGallery.types';
import { useRestrictedInternationalShippingWarningContext } from '../RestrictedInternationalShippingWarningProvider';
import type { StationeryProduct } from '@shared/utils/print-price/types';

type UseCustomizePremiumDigitalCardArgs = {
  eventId?: string;
  currentProduct: StationeryProduct | undefined;
  supportedFormats: Format;
};

/**
 * Both premium digital and the free Basic Digital V2 create a `digital` stationery
 * draft from the paper template and open the digital customizer -- the format only
 * changes telemetry and whether a `priceConfigId` is attached.
 */
type DigitalDraftFormat = 'premiumDigital' | 'basicDigitalV2';

export const useCustomizePremiumDigitalCard = (args: UseCustomizePremiumDigitalCardArgs) => {
  const { eventId, supportedFormats, currentProduct } = args;
  const { existingDraftsWarningDialog, cardDraftCreated, pdpCustomizeNowClicked, restrictedInternationalShippingWarningDialog } = useCardTelemetry({});

  const { goToDigitalCustomizeDraft } = useCardsRouterContext();
  const { checkForExistingDraftOrCreateNew } = useExistingDraftsWarningContext();
  const { checkIfUserIsRestricted } = useRestrictedInternationalShippingWarningContext();

  const [createStationeryDraft, { loading }] = useCreateStationeryDraftMutation({
    refetchQueries: () => ['GetDigitalAndPaperDraftsByEventId', 'GetExistingEventStationeryDraftsWithTemplate'],
  });

  const handleOnCustomizePremiumDigitalCardClick = useEventCallback(
    (template: MinimalRequiredTemplateDataForDraft, subtotalInMinorUnits?: number, format: DigitalDraftFormat = 'premiumDigital') => {
      const isFree = format === 'basicDigitalV2';
      checkIfUserIsRestricted(
        StationeryDraftFormat.digital,
        { themeId: template.themeId, category: template.category },
        () => {
          // This callback is called when the RestrictedInternationalShipping warning dialog is about to be shown.
          restrictedInternationalShippingWarningDialog.enter();
        },
        () => {
          checkForExistingDraftOrCreateNew(
            template.id,
            async existingDraftsCount => {
              try {
                pdpCustomizeNowClicked({ themeId: template.themeId, supportedFormats, format });
                // The free product stamps itself into the draft blob so the customizer can tell it
                // apart from premium digital -- the draft's own `format` cannot (both are `digital`).
                const cardJSON = isFree ? withBasicDigitalV2Stamp(template.themeJSON as ThemeJson) : (cloneDeep(template.themeJSON) as ThemeJson);

                if (!currentProduct) {
                  throw new Error('Tried creating draft with no selected product.');
                }

                const response = await createStationeryDraft({
                  variables: {
                    payload: {
                      eventId,
                      cardJSON,
                      stationeryTemplateId: template.id,
                      format: StationeryDraftFormat.digital,
                      // The free product is a client-side synthetic with no price config; leaving it
                      // off lets the customizer fall back to the template's real digital product.
                      priceConfigId: isFree ? undefined : currentProduct.priceConfigId,
                    },
                  },
                });
                const maybeDraftId = response.data?.createStationeryDraft.id;

                if (maybeDraftId) {
                  cardDraftCreated({
                    draftId: maybeDraftId,
                    themeId: template.themeId,
                    stationeryTemplateCategory: template.category,
                    isFavorite: !!template.isFavorite,
                    page: 'pdpModal',
                    existingDraftsCount,
                    subtotalInMinorUnits,
                    isDigital: true,
                    isPremiumDesign: isFree ? template.premium : true,
                    format,
                    cardWidthInches: template.widthInches,
                    cardHeightInches: template.heightInches,
                  });
                  goToDigitalCustomizeDraft(maybeDraftId);
                }
              } catch (error) {
                // Swallowed for the user (no error UI on this path today) but recorded, so a failing
                // draft creation isn't a silent dead-end in New Relic. Same in useCustomizePaperCard.
                addSuppressedError(error, { context: 'useCustomizePremiumDigitalCard', themeId: template.themeId });
              }
            },
            () => {
              // This callback is called when the warning dialog is about to be shown.
              existingDraftsWarningDialog.enter();
            },
            true,
          );
        },
      );
    },
  );

  return {
    handleOnCustomizePremiumDigitalCardClick,
    loading,
  };
};
